/**
 * 可由预览气泡承载的选择目标。
 */
export interface ChamferGuideSelectableTarget {
  /** 目标对应的选择项。 */
  selection: SelectionItemRef;
}

/**
 * 收集选择项上可用于回溯面的引用。
 *
 * @param selection 候选选择项。
 * @returns 去重后的相邻面引用。
 */
function collectSelectionAdjacentFaceRefs(selection: SelectionItemRef): Set<string> {
  const faceRefs = new Set<string>();

  /**
   * 面选择在求解前会被展开成边选择，原始面引用会进入 adjacentFaceRefs。
   * 这里统一读取静态字段和 runtime hint，避免 UI 选中面时气泡退回第一条边。
   */
  (selection.adjacentFaceRefs ?? []).forEach((faceRef) => {
    if (faceRef.length > 0) {
      faceRefs.add(faceRef);
    }
  });
  (selection.runtimeTopologyHint?.adjacentFaceRefs ?? []).forEach((faceRef) => {
    if (faceRef.length > 0) {
      faceRefs.add(faceRef);
    }
  });

  return faceRefs;
}

/**
 * 判断候选选择项是否对应当前优先引用。
 *
 * @param selection 候选选择项。
 * @param preferredItemRef 当前 UI 选中的选择项持久化引用。
 * @returns 是否命中当前优先引用。
 */
export function isSelectionLinkedToPreferredRef(
  selection: SelectionItemRef,
  preferredItemRef?: string | null,
): boolean {
  if (!preferredItemRef) {
    return false;
  }
  if (selection.persistentRef === preferredItemRef) {
    return true;
  }
  if (selection.runtimeTopologyHint?.persistentRef === preferredItemRef) {
    return true;
  }
  return collectSelectionAdjacentFaceRefs(selection).has(preferredItemRef);
}

/**
 * 从候选目标中选择最符合当前 UI 选中项的目标。
 *
 * @param targets 候选气泡目标。
 * @param preferredItemRef 当前 UI 选中的选择项持久化引用。
 * @returns 命中的目标；没有命中时回退第一项。
 */
export function choosePreferredChamferGuideTarget<TTarget extends ChamferGuideSelectableTarget>(
  targets: TTarget[],
  preferredItemRef?: string | null,
): TTarget | null {
  if (targets.length === 0) {
    return null;
  }
  if (!preferredItemRef) {
    return targets[0] ?? null;
  }
  return targets.find((target) => (
    isSelectionLinkedToPreferredRef(target.selection, preferredItemRef)
  )) ?? targets[0] ?? null;
}
